import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { Id, PatientIssue } from "../types";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { useDrawer } from "../context/DrawerContext";
import { formatDateTime } from "../lib/format";

type ScopeFilter = "open" | "resolved" | "all";

const SCOPES: { key: ScopeFilter; label: string }[] = [
  { key: "open", label: "Abiertas" },
  { key: "resolved", label: "Resueltas" },
  { key: "all", label: "Todas" },
];

// Resolving an issue is a human-only clinical/administrative action — never
// exposed as AI-actionable. URGENT (clinical) severity requires
// professional/admin/director per the backend's own gating (issues.js);
// reception may resolve NORMAL (administrative) issues only.
function canResolve(role: string | undefined, issue: PatientIssue): boolean {
  if (!role) return false;
  if (issue.severity === "URGENT") return ["admin", "director", "professional"].includes(role);
  return ["admin", "director", "professional", "reception"].includes(role);
}

export default function Incidencias() {
  const { user } = useAuth();
  const { show } = useToast();
  const { open } = useDrawer();
  const [scope, setScope] = useState<ScopeFilter>("open");
  const [issues, setIssues] = useState<PatientIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<Id | null>(null);
  const [notesById, setNotesById] = useState<Record<string, string>>({});

  const canCreate = !!user && ["admin", "director", "reception", "professional", "sales", "sales_manager"].includes(user.role_code);
  const [showCreate, setShowCreate] = useState(false);
  const [newContactId, setNewContactId] = useState("");
  const [newIssueType, setNewIssueType] = useState("");
  const [newSeverity, setNewSeverity] = useState<"NORMAL" | "URGENT">("NORMAL");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = scope === "all" ? {} : scope === "open" ? { status: undefined } : { status: "RESOLVED" };
      const rows = await api.getIssues(params);
      const filtered = scope === "open" ? rows.filter((r) => r.status !== "RESOLVED") : rows;
      setIssues(filtered);
    } catch (e) {
      if (e instanceof ApiClientError) setError(e.message);
      else setError("No se pudieron cargar las incidencias.");
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  async function resolve(issue: PatientIssue) {
    setResolvingId(issue.id);
    try {
      await api.resolveIssue(issue.id, { resolution_notes: notesById[String(issue.id)] || undefined });
      show("Incidencia resuelta.", "success");
      load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo resolver la incidencia.", "error");
    } finally {
      setResolvingId(null);
    }
  }

  async function createIssue() {
    if (!newContactId.trim() || !newIssueType.trim()) {
      show("Completá el paciente y el tipo de incidencia.", "error");
      return;
    }
    setCreating(true);
    try {
      await api.createIssue({
        contact_id: newContactId.trim(),
        issue_type: newIssueType.trim(),
        description: newDescription.trim() || undefined,
        severity: newSeverity,
      });
      show("Incidencia registrada.", "success");
      setNewContactId("");
      setNewIssueType("");
      setNewDescription("");
      setNewSeverity("NORMAL");
      setShowCreate(false);
      load();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar la incidencia.", "error");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="tasks-page">
      <h1>Incidencias de pacientes</h1>
      <p className="meta">
        Reclamos, reacciones y disconformidades detectadas en postventa. Resolverlas es una acción humana — la IA sólo
        contiene y deriva, nunca puede cerrarlas.
      </p>

      <div className="pipeline-tabs">
        {SCOPES.map((s) => (
          <button key={s.key} className={`tab ${scope === s.key ? "active" : ""}`} onClick={() => setScope(s.key)}>
            {s.label}
          </button>
        ))}
      </div>

      {canCreate && (
        <div className="dashboard-section" style={{ marginTop: 12 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ margin: 0 }}>Reportar incidencia manual</h2>
            <button className="btn-ghost" onClick={() => setShowCreate((v) => !v)}>
              {showCreate ? "Cancelar" : "Nueva incidencia"}
            </button>
          </div>
          {showCreate && (
            <div className="form-grid" style={{ maxWidth: 420, marginTop: 10 }}>
              <label>ID de paciente</label>
              <input placeholder="ID del contacto" value={newContactId} onChange={(e: any) => setNewContactId(e.target.value)} />
              <label>Tipo de incidencia</label>
              <input placeholder="ej. reaccion_adversa, reclamo" value={newIssueType} onChange={(e: any) => setNewIssueType(e.target.value)} />
              <label>Severidad</label>
              <select value={newSeverity} onChange={(e: any) => setNewSeverity(e.target.value)}>
                <option value="NORMAL">Normal</option>
                <option value="URGENT">Urgente (clínica)</option>
              </select>
              <label>Descripción</label>
              <textarea rows={2} value={newDescription} onChange={(e: any) => setNewDescription(e.target.value)} />
              <div />
              <button className="btn-primary" disabled={creating} onClick={createIssue}>
                {creating ? "Registrando..." : "Registrar incidencia"}
              </button>
            </div>
          )}
        </div>
      )}

      {loading && <div className="loading-strip">Cargando incidencias...</div>}
      {error && <p className="form-error">{error}</p>}

      <table className="task-table">
        <thead>
          <tr>
            <th>Severidad</th>
            <th>Paciente</th>
            <th>Tipo</th>
            <th>Origen</th>
            <th>Asignado a</th>
            <th>Abierta</th>
            <th>Estado</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => (
            <tr key={issue.id}>
              <td>
                <span className={`pill ${issue.severity === "URGENT" ? "pill-bad" : ""}`}>{issue.severity}</span>
              </td>
              <td>
                <button className="link-btn" onClick={() => open(issue.contact_id)}>
                  Paciente #{issue.contact_id}
                </button>
              </td>
              <td>{issue.issue_type}</td>
              <td className="meta">{issue.source || "-"}</td>
              <td className="meta">{issue.assigned_to ? `Usuario #${issue.assigned_to}` : "Sin asignar"}</td>
              <td className="meta">{formatDateTime(issue.opened_at)}</td>
              <td>
                <span className={`pill ${issue.status === "RESOLVED" ? "pill-ok" : "pill-warn"}`}>{issue.status}</span>
              </td>
              <td>
                {issue.status !== "RESOLVED" ? (
                  canResolve(user?.role_code, issue) ? (
                    <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                      <input
                        placeholder="Notas de resolución"
                        style={{ width: 140 }}
                        value={notesById[String(issue.id)] || ""}
                        onChange={(e: any) => setNotesById((m) => ({ ...m, [String(issue.id)]: e.target.value }))}
                      />
                      <button className="link-btn" disabled={resolvingId === issue.id} onClick={() => resolve(issue)}>
                        {resolvingId === issue.id ? "Resolviendo..." : "Resolver"}
                      </button>
                    </div>
                  ) : (
                    <span className="meta">Requiere profesional/Dirección</span>
                  )
                ) : (
                  <span className="meta">{issue.resolution_notes || "-"}</span>
                )}
              </td>
            </tr>
          ))}
          {!loading && issues.length === 0 && (
            <tr>
              <td colSpan={8} className="meta">
                No hay incidencias en esta vista.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
